// LA BASE DE LAS COLAS NUEVAS · DIRECTRIZ-PWA F2 (11 oct 2026, DISENO-F2 §6).
//
// POR QUÉ UNA BASE Y NO «GENERALIZAR cola.js». Las cuatro colas que ya
// existen (`cola.js`, `colaConteos.js`, `colaRecepciones.js`,
// `colaLecturas.js`) repiten a propósito las mismas líneas de IndexedDB,
// guardan cosas distintas y ya tienen datos en teléfonos. Tocarlas para
// darles un parámetro `tipo` cambiaría un módulo que funciona (Doctrina 1).
// Esta base es para las colas NUEVAS —traslados y ventas— y las viejas se
// leen tal cual desde el registro `integrador/pwa/pendientes.js`.
//
// LO QUE GARANTIZA, y es lo que prueban P1–P3:
//   · se guarda ANTES de cualquier envío (durabilidad primero, `cola.js:3`);
//   · la `clave_idempotencia` nace aquí, UNA vez, y es la clave del
//     almacén: un reintento no puede inventar otra;
//   · lo que el servidor rechaza por regla (400/403/404/409/410/422) NO se
//     reintenta: queda POR_REVISAR con el mensaje del servidor;
//   · lo que no llegó o falló del lado del servidor (red, 408, 425, 429,
//     401, 5xx) queda PENDIENTE con retroceso (5 s → 5 min). Nada se borra
//     solo: un servidor caído una tarde no puede convertirse en trabajo
//     perdido;
//   · un envío a la vez por cola; dos llamadas simultáneas hacen UN envío;
//   · cada cola va por persona (`id_usuario`): lo de otra cuenta se cuenta
//     y no se envía (R-PWA-5);
//   · al guardar el PRIMER elemento se vuelve a pedir el almacenamiento
//     protegido si la primera vez dijo que no (R-PWA-9), una vez por sesión.
import { ErrorApi } from '../api';
import { uuidv7 } from './uuidv7';
import { persistenciaRecordada, volverAPedirPersistencia } from '../integrador/pwa/instalacion';

export const PENDIENTE = 'PENDIENTE';
export const ENVIANDO = 'ENVIANDO';
export const POR_REVISAR = 'POR_REVISAR';
export const ENVIADA = 'ENVIADA';

export const EVENTO_CAMBIO = 'tego:cola-cambio';

// Cuánto se guarda lo ya enviado (para que «Pendientes de enviar» pueda
// decir «Enviada · folio 1234») antes de purgarlo.
export const VIDA_ENVIADA_MS = 24 * 60 * 60 * 1000;
// A partir de aquí la pantalla dice que algo no está llegando.
export const INTENTOS_PARA_AVISAR = 10;

const SE_REINTENTA = [0, 408, 425, 429, 401];

// P1-3 de AUDITORIA-F2: el almacén del teléfono puede fallar (lleno,
// bloqueado, ventana privada). Eso NO puede impedir trabajar CON señal: se
// envía directo y se dice que no quedó copia. Sin señal, se dice qué pasó y
// qué hacer, con estas palabras y no con «QuotaExceededError».
export const AVISO_SIN_RESPALDO = 'Este teléfono no pudo guardar una copia (su almacenamiento está lleno o bloqueado), así que se envió directo y sí quedó en TEGO.';
export const SIN_ALMACEN_SIN_SENAL = 'No se pudo guardar en este teléfono: su almacenamiento está lleno o bloqueado, y sin señal no hay otro sitio donde dejarlo. '
  + 'Qué hacer: libera espacio en el teléfono o sal de la ventana privada, o hazlo cuando vuelva la señal.';
export const SIN_ALMACEN_SIN_RESPUESTA = 'No se pudo guardar en este teléfono (su almacenamiento está lleno o bloqueado) y la respuesta de TEGO no llegó. '
  + 'Qué hacer: vuelve a intentarlo con señal; si ya había llegado, TEGO lo reconoce y no lo registra dos veces.';

/** El almacén del teléfono no dejó guardar. `causa` es el error del navegador. */
export class ErrorAlmacen extends Error {
  constructor(causa) {
    super(SIN_ALMACEN_SIN_SENAL);
    this.name = 'ErrorAlmacen';
    this.causa = causa;
  }
}

/** D-8: ¿este error deja el elemento PENDIENTE o lo manda a POR_REVISAR? */
export function clasificar(e) {
  if (!(e instanceof ErrorApi)) return PENDIENTE;
  if (SE_REINTENTA.includes(e.status) || e.status >= 500) return PENDIENTE;
  return POR_REVISAR;
}

/** ¿Ni siquiera hubo respuesta? (fetch fallido o sin status) */
export function noLlego(e) {
  return !(e instanceof ErrorApi) || !e.status;
}

/** Retroceso: 5 s, 10, 20, 40 … tope 5 min. `intentos` ≥ 1. */
export function retroceso(intentos) {
  return Math.min(5 * 60_000, 5_000 * 2 ** Math.max(0, intentos - 1));
}

// Lo que viaja: el cuerpo más la clave. Nunca los campos internos (`_ts`,
// `_intentos`…), ni `resumen`, ni `id_usuario`.
export function cargaDe(elemento) {
  return { ...elemento.cuerpo, clave_idempotencia: elemento.clave_idempotencia };
}

// R-PWA-9, segunda oportunidad: una vez por sesión, al primer elemento.
let persistenciaPedida = false;
// Dos guardados en el mismo milisegundo se ordenan por éste.
let secuencia = 0;
function pedirPersistenciaSiHaceFalta() {
  if (persistenciaPedida) return;
  if (persistenciaRecordada() !== false) return;
  persistenciaPedida = true;
  Promise.resolve(volverAPedirPersistencia()).catch(() => {});
}

// En el navegador `window`; en las pruebas de Node, la ventana montada.
const ventana = () => globalThis.window ?? globalThis;

function avisarCambio(db) {
  try {
    ventana().dispatchEvent?.(new CustomEvent(EVENTO_CAMBIO, { detail: { db } }));
  } catch { /* sin ventana: nadie escucha */ }
}

/**
 * Crea una cola sobre IndexedDB.
 *
 *   crearCola({
 *     db, store,
 *     enviar(elemento) → Promise<respuesta>,      // la petición de verdad
 *     resumen(cuerpo, extra) → objeto,             // lo que la pantalla enseña
 *     alEnviada?(elemento, respuesta) → objeto,    // campos a guardar en ENVIADA (folio…)
 *   })
 */
export function crearCola({ db, store, enviar, resumen = () => ({}), alEnviada = () => ({}) }) {
  function abrir() {
    return new Promise((res, rej) => {
      const r = indexedDB.open(db, 1);
      r.onupgradeneeded = () => {
        if (!r.result.objectStoreNames.contains(store)) {
          r.result.createObjectStore(store, { keyPath: 'clave_idempotencia' });
        }
      };
      r.onsuccess = () => res(r.result);
      r.onerror = () => rej(r.error);
    });
  }
  function conStore(modo, fn) {
    return abrir().then((d) => new Promise((res, rej) => {
      const t = d.transaction(store, modo);
      const rq = fn(t.objectStore(store));
      t.oncomplete = () => { d.close(); res(rq && rq.result); };
      t.onerror = () => { d.close(); rej(t.error); };
      t.onabort = () => { d.close(); rej(t.error); };
    }));
  }
  const escribir = async (e) => { await conStore('readwrite', (s) => s.put(e)); avisarCambio(db); return e; };
  const borrar = async (clave) => { await conStore('readwrite', (s) => s.delete(clave)); avisarCambio(db); };
  const leerUno = (clave) => conStore('readonly', (s) => s.get(clave));
  // P1-2 de AUDITORIA-F2: el estado final de un envío se escribe SOLO si la
  // fila sigue ahí, releída dentro de la misma transacción. Si la persona la
  // descartó mientras iba en vuelo, terminar el envío no la vuelve a crear:
  // «no se enviará nunca» sigue siendo verdad en este teléfono.
  const escribirSiSigue = async (e) => {
    let escrita = false;
    await conStore('readwrite', (s) => {
      const rq = s.get(e.clave_idempotencia);
      rq.onsuccess = () => { if (rq.result) { s.put(e); escrita = true; } };
      return rq;
    });
    if (escrita) avisarCambio(db);
    return escrita ? e : null;
  };
  const leerTodo = async () => (await conStore('readonly', (s) => s.getAll())) || [];

  // Las claves que ESTE proceso está enviando ahora mismo. Un ENVIANDO que
  // no esté aquí es un fantasma de una pestaña que se cerró a medias: se
  // lee como PENDIENTE.
  const enVuelo = new Set();
  let enCurso = null;

  // SE RELEE DENTRO DE LA TRANSACCIÓN. Una lectura hecha un instante antes
  // puede decir ENVIANDO cuando el envío ya terminó: escribir PENDIENTE
  // desde esa copia pisaría una ENVIADA (pasó al estrenar la pantalla:
  // el evento de cambio hacía releer la lista a mitad de un envío).
  function normalizar(clave) {
    return conStore('readwrite', (s) => {
      const rq = s.get(clave);
      rq.onsuccess = () => {
        const e = rq.result;
        if (e && e.estado === ENVIANDO && !enVuelo.has(clave)) s.put({ ...e, estado: PENDIENTE });
      };
      return rq;
    });
  }

  /** Todo lo guardado, normalizado y purgado, de la más vieja a la más nueva. */
  async function todos() {
    const ahora = Date.now();
    let tocado = false;
    for (const e of await leerTodo()) {
      if (e.estado === ENVIADA && ahora - (e._enviada_en || e._ts || 0) > VIDA_ENVIADA_MS) {
        await conStore('readwrite', (s) => s.delete(e.clave_idempotencia));
        tocado = true;
      } else if (e.estado === ENVIANDO && !enVuelo.has(e.clave_idempotencia)) {
        await normalizar(e.clave_idempotencia);
        tocado = true;
      }
    }
    const salida = await leerTodo();
    void tocado;
    // Desempate dentro del mismo milisegundo: el orden en que se guardó.
    return salida.sort((a, b) => ((a._ts || 0) - (b._ts || 0)) || ((a._seq || 0) - (b._seq || 0)));
  }

  const deUsuario = (lista, idUsuario) => lista.filter((e) => String(e.id_usuario) === String(idUsuario));

  // `opciones.clave`: reutilizar una clave ya nacida (el reintento de un
  // cobro cuya respuesta no llegó, P2-4). Por omisión, una nueva.
  async function guardar(idUsuario, cuerpo, extra = {}, opciones = {}) {
    if (idUsuario === undefined || idUsuario === null || idUsuario === '') {
      throw new Error('Para guardar sin señal hace falta una sesión abierta. Vuelve a entrar a TEGO con señal.');
    }
    let habia;
    try { habia = (await leerTodo()).length; } catch (err) { throw new ErrorAlmacen(err); }
    const e = {
      ...extra,
      clave_idempotencia: opciones.clave || uuidv7(),
      id_usuario: idUsuario,
      cuerpo,
      resumen: resumen(cuerpo, extra),
      estado: PENDIENTE,
      _ts: Date.now(),
      _seq: (secuencia += 1),
      _intentos: 0,
      _ultimo_intento: null,
      _ultimo_error: null,
      _proximo_intento: 0,
    };
    try { await escribir(e); } catch (err) { throw new ErrorAlmacen(err); }
    if (habia === 0) pedirPersistenciaSiHaceFalta();
    return e;
  }

  /**
   * Sin copia en el teléfono: la petición directa, con una clave nacida aquí.
   * Solo con señal y solo cuando el almacén falló.
   */
  async function enviarSinCopia(idUsuario, cuerpo, extra = {}, opciones = {}) {
    const e = { ...extra, clave_idempotencia: opciones.clave || uuidv7(), id_usuario: idUsuario, cuerpo };
    try {
      return { respuesta: await enviar(e), elemento: e };
    } catch (err) {
      if (clasificar(err) === PENDIENTE) {
        const otro = new Error(SIN_ALMACEN_SIN_RESPUESTA);
        otro.causa = err;
        otro.elemento = e;
        throw otro;
      }
      throw err;
    }
  }

  /**
   * Un envío de UN elemento, con su cambio de estado. Devuelve
   *   {estado:'aplicado', elemento, respuesta}
   *   {estado:'pendiente', elemento, error}      red / 5xx / 429 / 401
   *   {estado:'por_revisar', elemento, error}    rechazo por regla
   * `enviada`: si al aplicarse se conserva como ENVIADA (true) o se borra.
   */
  async function intentar(elemento, { enviada = true } = {}) {
    const clave = elemento.clave_idempotencia;
    enVuelo.add(clave);
    // Tampoco se marca ENVIANDO lo que se descartó entre la lectura y aquí.
    const marcado = await escribirSiSigue({ ...elemento, estado: ENVIANDO });
    if (!marcado) {
      enVuelo.delete(clave);
      return { estado: 'descartado', elemento };
    }
    try {
      const respuesta = await enviar(marcado);
      enVuelo.delete(clave);
      if (enviada) {
        const datos = {
          ...marcado, estado: ENVIADA, _enviada_en: Date.now(), _ultimo_error: null,
          ...(alEnviada(marcado, respuesta) || {}),
        };
        const final = await escribirSiSigue(datos);
        return { estado: 'aplicado', elemento: final || datos, respuesta, descartado: !final };
      }
      await borrar(clave);
      return { estado: 'aplicado', elemento: { ...marcado, estado: ENVIADA }, respuesta };
    } catch (e) {
      enVuelo.delete(clave);
      const intentos = (marcado._intentos || 0) + 1;
      if (clasificar(e) === POR_REVISAR) {
        const datos = {
          ...marcado, estado: POR_REVISAR, _intentos: intentos, _ultimo_intento: Date.now(),
          _ultimo_error: e.message, motivo: e.message, _rechazada_en: Date.now(), _proximo_intento: 0,
        };
        const final = await escribirSiSigue(datos);
        return { estado: 'por_revisar', elemento: final || datos, error: e, descartado: !final };
      }
      const datos = {
        ...marcado, estado: PENDIENTE, _intentos: intentos, _ultimo_intento: Date.now(),
        _ultimo_error: e.message,
        _proximo_intento: noLlego(e) ? 0 : Date.now() + retroceso(intentos),
      };
      const final = await escribirSiSigue(datos);
      return { estado: 'pendiente', elemento: final || datos, error: e, descartado: !final };
    }
  }

  /**
   * Guarda y, si hay señal, intenta. Devuelve
   *   {estado:'aplicado', respuesta}  — el servidor lo tiene
   *   {estado:'encolado', elemento}   — quedó en el teléfono
   * Lanza el ErrorApi si el servidor lo rechaza por regla (y lo borra: no
   * fue sin señal, la persona lo está viendo).
   */
  async function registrar(idUsuario, cuerpo, extra = {}) {
    let e;
    try {
      e = await guardar(idUsuario, cuerpo, extra);
    } catch (err) {
      if (!(err instanceof ErrorAlmacen) || globalThis.navigator?.onLine === false) throw err;
      const r = await enviarSinCopia(idUsuario, cuerpo, extra);
      const respuesta = r.respuesta && typeof r.respuesta === 'object'
        ? { ...r.respuesta, mensaje: [r.respuesta.mensaje, AVISO_SIN_RESPALDO].filter(Boolean).join(' ') }
        : r.respuesta;
      return { estado: 'aplicado', respuesta, elemento: { ...r.elemento, estado: ENVIADA }, sinRespaldo: true };
    }
    if (globalThis.navigator?.onLine === false) return { estado: 'encolado', elemento: e };
    const r = await intentar(e, { enviada: false });
    if (r.estado === 'aplicado') return { estado: 'aplicado', respuesta: r.respuesta, elemento: r.elemento };
    if (r.estado === 'por_revisar') {
      await borrar(e.clave_idempotencia);
      throw r.error;
    }
    return { estado: 'encolado', elemento: r.elemento };
  }

  async function mandarPendientes(idUsuario, { forzar = null } = {}) {
    const ahora = Date.now();
    let enviados = 0;
    const rechazados = [];
    for (const e of deUsuario(await todos(), idUsuario)) {
      if (e.estado !== PENDIENTE) continue;
      if (forzar ? e.clave_idempotencia !== forzar : (e._proximo_intento || 0) > ahora) continue;
      const r = await intentar(e);
      if (r.estado === 'descartado') continue;
      if (r.estado === 'aplicado') { enviados += 1; continue; }
      if (r.estado === 'por_revisar') { if (!r.descartado) rechazados.push(r.elemento); continue; }
      // Sin respuesta útil del servidor: las demás tampoco van a llegar, y
      // seguir desordenaría los reenvíos (misma regla que colaLecturas).
      break;
    }
    return { enviados, rechazados, restantes: await contar(idUsuario) };
  }

  /** Reenvía lo PENDIENTE de esta persona. Un envío a la vez. */
  function reintentar(idUsuario, opciones = {}) {
    if (globalThis.navigator?.onLine === false) {
      return contar(idUsuario).then((restantes) => ({ enviados: 0, rechazados: [], restantes }));
    }
    if (!enCurso) {
      enCurso = mandarPendientes(idUsuario, opciones).finally(() => { enCurso = null; });
      return enCurso;
    }
    // Ya hay un envío en marcha: quien llega segundo espera al primero y
    // NO manda nada por su cuenta.
    return enCurso;
  }

  /** «Enviar ahora»: ese elemento sin esperar su retroceso. */
  async function enviarAhora(clave) {
    const e = await leerUno(clave);
    if (!e) return { enviados: 0, rechazados: [], restantes: 0 };
    if (enCurso) await enCurso.catch(() => {});
    enCurso = mandarPendientes(e.id_usuario, { forzar: clave }).finally(() => { enCurso = null; });
    return enCurso;
  }

  async function listar(idUsuario, { estado } = {}) {
    const lista = deUsuario(await todos(), idUsuario);
    return estado ? lista.filter((e) => e.estado === estado) : lista;
  }
  async function contar(idUsuario) {
    return (await listar(idUsuario)).filter((e) => e.estado === PENDIENTE || e.estado === ENVIANDO).length;
  }
  async function contarPorRevisar(idUsuario) {
    return (await listar(idUsuario, { estado: POR_REVISAR })).length;
  }
  async function contarDeOtraCuenta(idUsuario) {
    return (await todos()).filter(
      (e) => String(e.id_usuario) !== String(idUsuario) && e.estado !== ENVIADA,
    ).length;
  }
  async function marcarPorRevisar(clave, motivo) {
    const e = await leerUno(clave);
    if (!e) return null;
    return escribir({ ...e, estado: POR_REVISAR, motivo, _ultimo_error: motivo, _rechazada_en: Date.now() });
  }
  /** Reescribe campos de un elemento conservando su clave y su `_ts`. */
  async function actualizar(clave, cambios) {
    const e = await leerUno(clave);
    if (!e) return null;
    return escribir({ ...e, ...cambios, clave_idempotencia: clave, _ts: e._ts });
  }
  function suscribir(fn) {
    const oir = (ev) => { if (!ev?.detail?.db || ev.detail.db === db) fn(ev); };
    ventana().addEventListener?.(EVENTO_CAMBIO, oir);
    return () => ventana().removeEventListener?.(EVENTO_CAMBIO, oir);
  }

  return {
    db, store,
    guardar, registrar, intentar, reintentar, enviarAhora, enviarSinCopia,
    listar, leer: leerUno, contar, contarPorRevisar, contarDeOtraCuenta,
    descartar: borrar, marcarPorRevisar, actualizar, suscribir,
  };
}

/* Solo para pruebas: olvida que ya se pidió la persistencia esta sesión. */
export function _reiniciarColaBase() {
  persistenciaPedida = false;
}
